import { supabase } from './supabase';

export const seasonManagementService = {
  async createSeason(seasonNumber, name, year) {
    try {
      const { data, error } = await supabase
        .from('seasons')
        .insert([{ season_number: seasonNumber, name, year, is_active: false }])
        .select();

      if (error) {
        console.error('Error creating season:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in createSeason:', err);
      throw err;
    }
  },

  async updateSeason(seasonId, updates) {
    try {
      const { data, error } = await supabase
        .from('seasons')
        .update(updates)
        .eq('id', seasonId)
        .select();

      if (error) {
        console.error('Error updating season:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in updateSeason:', err);
      throw err;
    }
  },

  async createConference(name, abbreviation) {
    try {
      const { data, error } = await supabase
        .from('conferences')
        .insert([{ name, abbreviation }])
        .select();

      if (error) {
        console.error('Error creating conference:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in createConference:', err);
      throw err;
    }
  },

  async updateConference(conferenceId, updates) {
    try {
      const { data, error } = await supabase
        .from('conferences')
        .update(updates)
        .eq('id', conferenceId)
        .select();

      if (error) {
        console.error('Error updating conference:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in updateConference:', err);
      throw err;
    }
  },

  async deleteConference(conferenceId) {
    try {
      const { error } = await supabase
        .from('conferences')
        .delete()
        .eq('id', conferenceId);

      if (error) {
        console.error('Error deleting conference:', error);
        throw error;
      }
    } catch (err) {
      console.error('Error in deleteConference:', err);
      throw err;
    }
  },

  async createTeam(name, abbreviation, conferenceId, logoUrl = null) {
    try {
      const { data, error } = await supabase
        .from('teams')
        .insert([{ name, abbreviation, conference_id: conferenceId, logo_url: logoUrl }])
        .select();

      if (error) {
        console.error('Error creating team:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in createTeam:', err);
      throw err;
    }
  },

  async updateTeam(teamId, updates) {
    try {
      const { data, error } = await supabase
        .from('teams')
        .update(updates)
        .eq('id', teamId)
        .select();

      if (error) {
        console.error('Error updating team:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in updateTeam:', err);
      throw err;
    }
  },

  async deleteTeam(teamId) {
    try {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);

      if (error) {
        console.error('Error deleting team:', error);
        throw error;
      }
    } catch (err) {
      console.error('Error in deleteTeam:', err);
      throw err;
    }
  },

  async addTeamToStandings(seasonId, teamId, wins = 0, losses = 0, pointsFor = 0, pointsAgainst = 0) {
    try {
      const { data, error } = await supabase
        .from('team_standings')
        .insert([{
          season_id: seasonId,
          team_id: teamId,
          wins,
          losses,
          points_for: pointsFor,
          points_against: pointsAgainst
        }])
        .select();

      if (error) {
        console.error('Error adding team to standings:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in addTeamToStandings:', err);
      throw err;
    }
  },

  async updateTeamStandings(standingsId, updates) {
    try {
      const { data, error } = await supabase
        .from('team_standings')
        .update(updates)
        .eq('id', standingsId)
        .select();

      if (error) {
        console.error('Error updating standings:', error);
        throw error;
      }

      return data[0];
    } catch (err) {
      console.error('Error in updateTeamStandings:', err);
      throw err;
    }
  },

  async getTeamsForSeason(seasonId) {
    try {
      const { data, error } = await supabase
        .from('team_standings')
        .select(`
          *,
          teams (
            id,
            name,
            abbreviation,
            logo_url,
            conferences (
              id,
              name,
              abbreviation
            )
          )
        `)
        .eq('season_id', seasonId)
        .order('rank', { ascending: true });

      if (error) {
        console.error('Error fetching teams for season:', error);
        throw error;
      }

      return data || [];
    } catch (err) {
      console.error('Error in getTeamsForSeason:', err);
      throw err;
    }
  },

  async addAllTeamsToSeason(seasonId) {
    try {
      const { data: allTeams, error: teamsError } = await supabase
        .from('teams')
        .select('id');

      if (teamsError) {
        console.error('Error fetching teams:', teamsError);
        throw teamsError;
      }

      const { data: existingTeams, error: existingError } = await supabase
        .from('team_standings')
        .select('team_id')
        .eq('season_id', seasonId);

      if (existingError) {
        console.error('Error fetching existing standings:', existingError);
        throw existingError;
      }

      const existingTeamIds = new Set(existingTeams.map(t => t.team_id));
      const teamStandingsToInsert = allTeams
        .filter(team => !existingTeamIds.has(team.id))
        .map(team => ({
          season_id: seasonId,
          team_id: team.id,
          wins: 0,
          losses: 0,
          points_for: 0,
          points_against: 0
        }));

      if (teamStandingsToInsert.length === 0) {
        return { message: 'All teams already added to season' };
      }

      const { data, error } = await supabase
        .from('team_standings')
        .insert(teamStandingsToInsert)
        .select();

      if (error) {
        console.error('Error adding teams to standings:', error);
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Error in addAllTeamsToSeason:', err);
      throw err;
    }
  },

  async getAllTeams() {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select(`
          *,
          conferences (
            id,
            name,
            abbreviation
          )
        `)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching all teams:', error);
        throw error;
      }

      return data || [];
    } catch (err) {
      console.error('Error in getAllTeams:', err);
      throw err;
    }
  },

  async resetSeasonStandings(seasonId) {
    try {
      const { data, error } = await supabase
        .from('team_standings')
        .update({
          wins: 0,
          losses: 0,
          points_for: 0,
          points_against: 0
        })
        .eq('season_id', seasonId)
        .select();

      if (error) {
        console.error('Error resetting season standings:', error);
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Error in resetSeasonStandings:', err);
      throw err;
    }
  }
};
